import React from "react";
import {
    ClinicianCredential,
    IAvailabilitySchedule,
    IClinician,
    RecurrenceRule,
} from "intake_shared/src/types";
import { Dialog } from "../../../bits/Dialog/Dialog";
import { Select } from "../../../bits/Select/Select";
import CustomDateSelector from "../../../bits/CustomDateSelector/CustomDateSelector";
import { getAvailabilityRecurrenceOptions } from "../../../../v2utils";

interface AddAvailabilityDialogProps {
    open: boolean;
    onClose: () => void;
    loggedInClinician: IClinician;
    availabilitySchedules: IAvailabilitySchedule[];
    heldBookingSchedules: IAvailabilitySchedule[];
    confirmedBookingSchedules: IAvailabilitySchedule[];
    officeWindowSchedules: IAvailabilitySchedule[];
    clinicianServiceSelection: { [key: string]: { services: string[] } };
    requestedServices: { [key: string]: string };
    onAvailabilityAdded: () => void;
    draftAvailability: any;
    setDraftAvailability: (data: any) => void;
    onSave: () => void;
}

interface Option {
    value: string;
    label: string;
}

export default function AddAvailabilityDialog({
    open,
    onClose,
    loggedInClinician,
    availabilitySchedules,
    heldBookingSchedules,
    confirmedBookingSchedules,
    officeWindowSchedules,
    clinicianServiceSelection,
    requestedServices,
    onAvailabilityAdded,
    draftAvailability,
    setDraftAvailability,
    onSave,
}: AddAvailabilityDialogProps) {
    const canSetAvailabilityEndDate =
        loggedInClinician.credential === ClinicianCredential.INTERN;
    const allowAnyServiceOneOff =
        loggedInClinician.departments?.includes("CHP") ?? false;

    const serviceOptions = [
        { value: "", label: "Any service" },
        ...(clinicianServiceSelection[loggedInClinician.id!]?.services?.map(
            (serviceKey) => ({
                value: serviceKey,
                label: requestedServices[serviceKey] || serviceKey,
            })
        ) || []),
    ];

    const recurrenceOptions = getAvailabilityRecurrenceOptions(
        draftAvailability.availableService,
        requestedServices,
        allowAnyServiceOneOff
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title="Set availability"
            onSave={onSave}
            onCancel={onClose}
            saveText="Save Availability"
            cancelText="Cancel"
        >
            <div className="v2-form">
                <div className="gridTwo">
                    <Select
                        label="Service"
                        value={serviceOptions.find(
                            (option) =>
                                option.value === draftAvailability.availableService
                        )}
                        onChange={(value) => {
                            if (value) {
                                const availableService = (value as Option).value;
                                const availableRecurrenceOptions =
                                    getAvailabilityRecurrenceOptions(
                                        availableService,
                                        requestedServices,
                                        allowAnyServiceOneOff
                                    );
                                setDraftAvailability({
                                    ...draftAvailability,
                                    availableService,
                                    recurrenceRule: availableRecurrenceOptions.some(
                                        (option) =>
                                            option.value ===
                                            draftAvailability.recurrenceRule
                                    )
                                        ? draftAvailability.recurrenceRule
                                        : RecurrenceRule.WEEKLY,
                                });
                            }
                        }}
                        options={serviceOptions}
                        isLongListInDialog
                    />
                    <Select
                        label="Recurring"
                        value={recurrenceOptions.find(
                            (option) =>
                                option.value === draftAvailability.recurrenceRule
                        )}
                        onChange={(option) => {
                            if (
                                option &&
                                typeof option === "object" &&
                                "value" in option
                            ) {
                                setDraftAvailability({
                                    ...draftAvailability,
                                    recurrenceRule: option.value as RecurrenceRule,
                                });
                            }
                        }}
                        options={recurrenceOptions}
                        isLongListInDialog
                    />
                </div>

                <CustomDateSelector
                    showEndingDate={canSetAvailabilityEndDate}
                    onChange={(from, to, endDate) => {
                        setDraftAvailability({
                            ...draftAvailability,
                            from,
                            to,
                            endDate,
                        });
                    }}
                    departments={loggedInClinician.departments}
                />
            </div>
        </Dialog>
    );
}
